import React, { useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider, useApp } from './context/AppContext';

import { useFlowClient } from './hooks/useFlowClient';
import { LoadingSpinner } from './components/Layout';
import { getDemoSessionToken } from './utils/demoSession';
import { initClarity, trackEvent } from './utils/clarity';
import Login from './screens/Login';
import Landing from './screens/Landing';
import Portfolio from './screens/Portfolio';
import Dashboard from './screens/Dashboard';
import Weekly from './screens/Weekly';
import Ask from './screens/Ask';
import Report from './screens/Report';
import Upload from './screens/Upload';
import Account from './screens/Account';
import Admin from './screens/Admin';

// Real, server-backed demo mode — a session token minted by POST /api/demo/session
// (see Landing.tsx's "Try a Sample Schedule" button). Evaluated once at module load,
// same as the old URL-flag approach; entering/leaving demo mode is a full page
// navigation (Landing.tsx / AppHeader's "Exit demo"), so this is never stale mid-session.
const IS_DEMO = !!getDemoSessionToken();

/* ─── User data loader ─── */
const UserDataLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, userEmail } = useAuth();
  const { user, setUser, setProjects, setUserDataLoading, setUserDataError } = useApp();
  const { inProgress } = useMsal();
  const flowClient = useFlowClient();

  const fetchUserData = useCallback(async (email: string) => {
    setUserDataLoading(true);
    setUserDataError(null);
    try {
      const res = await flowClient.projects(email);
      setUser({
        email,
        name: res.user?.name || email.split('@')[0],
        role: res.user?.role === 'PM' ? 'PM' : 'GC',
        company: res.user?.company || '',
      });
      if (Array.isArray(res.projects)) {
        setProjects(res.projects);
      }
      // Only on genuine success — a failed demo session request never reaches here.
      if (IS_DEMO) trackEvent('demo_entered');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load user data.';
      setUserDataError(msg);
      setUser({
        email,
        name: email.split('@')[0],
        role: 'GC',
        company: '',
      });
    } finally {
      setUserDataLoading(false);
    }
  }, [flowClient, setUser, setProjects, setUserDataLoading, setUserDataError]);

  useEffect(() => {
    // Demo mode calls the real /api/projects like any other visitor — useFlowClient
    // already attaches X-Demo-Session instead of a JWT, and the server resolves the
    // fixed demo identity from that token, not from the email passed here.
    if (IS_DEMO && !user) {
      fetchUserData('demo-visitor@buildahead.demo');
      return;
    }
    if (isAuthenticated && userEmail && !user && inProgress === InteractionStatus.None) {
      fetchUserData(userEmail);
    }
  }, [isAuthenticated, userEmail, user, inProgress, fetchUserData]);

  return <>{children}</>;
};

/* ─── App routes ─── */
const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { user, userDataLoading } = useApp();
  const { inProgress } = useMsal();

  const isMsalBusy = inProgress !== InteractionStatus.None;

  if (isMsalBusy) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-ground dark:bg-gray-950">
        <LoadingSpinner message="Signing you in…" />
      </div>
    );
  }

  if (!isAuthenticated && !IS_DEMO) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  if (userDataLoading || !user) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-ground dark:bg-gray-950">
        <LoadingSpinner message="Loading your projects…" />
      </div>
    );
  }

  // Everyone — any USERS.role, first login or later, demo or signed in — lands on the
  // one Portfolio page. USERS.role is no longer a routing input: access and role are
  // per-project (PROJECT_ACCESS). /projects was the old role-based PM landing page
  // (ProjectSelector); it now redirects so old bookmarks keep working.
  const defaultHome = '/portfolio';

  return (
    <Routes>
      <Route path="/" element={<Navigate to={defaultHome} replace />} />
      <Route path="/portfolio" element={<Portfolio />} />
      <Route path="/projects" element={<Navigate to="/portfolio" replace />} />
      <Route path="/project/:project_id/dashboard" element={<Dashboard />} />
      <Route path="/project/:project_id/lookahead" element={<Weekly />} />
      <Route path="/project/:project_id/ask" element={<Ask />} />
      <Route path="/project/:project_id/reports" element={<Report />} />
      <Route path="/project/:project_id/upload" element={<Upload />} />
      <Route path="/account" element={<Account />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="*" element={<Navigate to={defaultHome} replace />} />
    </Routes>
  );
};

/* ─── Root App ─── */
const App: React.FC = () => {
  useEffect(() => { initClarity(); }, []);

  return (
    <BrowserRouter>
      <AppProvider>
        <AuthProvider>
          <UserDataLoader>
            <AppRoutes />
          </UserDataLoader>
        </AuthProvider>
      </AppProvider>
    </BrowserRouter>
  );
};

export default App;
